import { cameraApi } from "@/core/cameraApi";
import type { I18nKey } from "@/core/i18n";
import { useAppDispatch } from "@/hooks/redux";
import { Button } from "@/modules/shadcn/ui/button";
import { Project } from "@repo/schema";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { validateCameraApiUrl } from "../../utils/validateCameraApiUrl";
import { useUpdateProjectMutation } from "../../services/projectApi";
import { Modal } from "../Modal";
import { ProjectDetailsForm } from "../ProjectDetailsForm";
import { apiErrorMessage } from "@/core/api";

interface EditProjectModalProps {
  project: Project;
  onClose: () => void;
}

export const EditProjectModal = ({
  project,
  onClose,
}: EditProjectModalProps) => {
  const { t } = useTranslation(["project", "common"]);
  const dispatch = useAppDispatch();

  const [name, setName] = useState(project.name);
  const [description, setDescription] = useState(project.description || "");
  const [deviceId, setDeviceId] = useState<number | null>(project.deviceId);
  const [cameraApiUrl, setCameraApiUrl] = useState<string | null>(
    project.cameraApiUrl,
  );
  const [cameraApiUrlError, setCameraApiUrlError] =
    useState<I18nKey<"project"> | null>(null);
  const [selectedCameraMxid, setSelectedCameraMxid] = useState<string | null>(
    project.cameraMxid,
  );

  const [updateProject, { isLoading: isUpdating }] = useUpdateProjectMutation();

  const handleCameraApiUrlChange = (val: string) => {
    setCameraApiUrl(val);
    if (cameraApiUrlError) setCameraApiUrlError(validateCameraApiUrl(val));
  };

  const handleCameraApiUrlBlur = () => {
    setCameraApiUrlError(validateCameraApiUrl(cameraApiUrl));
  };

  /**
   * A device and a local address are alternatives, not a pair.
   *
   * Saving both is what let the app resolve to a LAN address that a
   * relay-backed project has no reason to be able to reach. Switching target
   * also invalidates the chosen camera, which is cleared here in the handler
   * rather than in an effect — an effect would wipe the saved mxid on first
   * render.
   */
  const handleDeviceChange = (next: number | null) => {
    if (next === deviceId) {
      return;
    }

    setDeviceId(next);
    setSelectedCameraMxid(null);

    // The address is dropped by the save, not here: clearing the field would
    // lose what the user typed if they switch back to the local network, and
    // the field is hidden while a device is selected anyway. Its *error* does
    // have to go — it disables Save, and a stale address must not block the
    // save that replaces it with a device.
    if (next !== null) {
      setCameraApiUrlError(null);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) return;

    // With a device chosen the address is being cleared, so there is nothing to
    // validate — and a stale invalid value must not block the save that fixes
    // the project.
    if (deviceId === null) {
      const urlError = validateCameraApiUrl(cameraApiUrl);
      if (urlError) {
        setCameraApiUrlError(urlError);
        return;
      }
    }

    const normalizedUrl =
      deviceId !== null ? null : (cameraApiUrl ?? "").trim() || null;

    try {
      await updateProject({
        projectId: project.id,
        name,
        description,
        deviceId,
        cameraApiUrl: normalizedUrl,
        cameraMxid: selectedCameraMxid,
        multipleDashboardConfigs: project.multipleDashboardConfigs,
      }).unwrap();

      // Eagerly reset cameraApi when the project URL changes so any in-flight
      // request against the old URL is aborted before the project list refetches.
      // Switching device changes the base URL just as surely as editing the
      // address does, so both have to drop in-flight requests aimed at the old
      // camera before the project list refetches.
      if (normalizedUrl !== project.cameraApiUrl || deviceId !== project.deviceId) {
        dispatch(cameraApi.util.resetApiState());
      }

      onClose();
    } catch (error) {
      console.error("Update failed:", error);
      toast.error(apiErrorMessage(error, t("editModal.failed")));
    }
  };

  const buttons = (
    <div className="flex flex-row gap-3">
      <Button variant="destructive" size="sm" onClick={onClose}>
        {t("common:actions.cancel")}
      </Button>
      <Button
        size="sm"
        onClick={handleSave}
        disabled={isUpdating || !name.trim() || !!cameraApiUrlError}
      >
        {isUpdating ? t("saving") : t("editModal.saveChanges")}
      </Button>
    </div>
  );

  return (
    <Modal
      title={t("editModal.title")}
      closeButton={false}
      buttons={buttons}
      onClose={onClose}
    >
      <ProjectDetailsForm
        name={name}
        setName={setName}
        description={description}
        setDescription={setDescription}
        deviceId={deviceId}
        setDeviceId={handleDeviceChange}
        cameraApiUrl={cameraApiUrl}
        setCameraApiUrl={handleCameraApiUrlChange}
        cameraApiUrlError={cameraApiUrlError}
        onCameraApiUrlBlur={handleCameraApiUrlBlur}
        selectedCameraMxid={selectedCameraMxid}
        setSelectedCameraMxid={setSelectedCameraMxid}
        revokedLocalAddress={project.cameraApiUrl}
      />
    </Modal>
  );
};
